import {
  AddIcon,
  Button,
  Card,
  Grid,
  GridColumn,
  GridRow,
  ImportIcon,
  LazyLoader,
  Modal,
  SearchInput,
  Skeleton,
  Tab,
  Tooltip,
} from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { Suspense, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ExclusionTimeframeForm } from '../../../../../../components/ExclusionTimeframe/ExclusionTimeframeForm';
import { useTeachingBlockStore } from '../../../../../../components/TeachingBlockGrid/Contexts/TeachingBlockContext';
import { Exclusions } from '../../../../../../components/TeachingBlockVersion/Exclusions/Exclusions';
import { TeachingBlockVersionTableType } from '../../../../../../components/TeachingBlockVersion/graphql/types';
import { TeachingBlockVersionSubjectsTable } from '../../../../../../components/TeachingBlockVersion/TeachingBlockVersionDataTables/TeachingBlockVersionSubjectsTable';
import { useAuthClaims } from '../../../../../../hooks/useAuthClaims';
import { useLoadBasicData } from '../../../../../../hooks/useLoadBasicData';
import { useMemorizedCacheTag } from '../../../../../../hooks/useMemorizedCacheTag';
import {
  SortDirection,
  use_TeachingBlockAvailabilityExclusionsQuery,
  useConnectEntityWithVersionMutation,
} from '../../../../../../types/planung-graphql-client-defs';
import { hexToColorOption } from '../../../../../../utils/colorUtils';
import { createLessonFilterUrl } from '../../../../../../utils/create-lesson-filter-url';
import { showSuccessSaveToast, showUserErrorToast } from '../../../../../../utils/toast';
import styles from '../TeachingBlockVersionData.module.scss';

export const PlanTeachingBlockSubjects = observer(() => {
  const { t } = useTranslation();
  const { teachingBlockVersionUuid } = useParams();
  const { pimAuthClaims } = useAuthClaims();

  const navigate = useNavigate();

  const [tab, setTab] = useState<'exclusion' | 'condition'>('exclusion');
  const [exclusionModalOpen, setExclusionModalOpen] = useState<boolean>(false);
  const [selectedExclusionUuid, setSelectedExclusionUuid] = useState<string | null>(null);
  const [search, setSearch] = useState<string>('');

  const context = useMemorizedCacheTag('TEACHINGBLOCK_VERSIONS');
  const availabilityContext = useMemorizedCacheTag('TEACHING_BLOCK_AVAILABILITY_EXCLUSION');

  const [{ data: exclusionsData }] = use_TeachingBlockAvailabilityExclusionsQuery({
    variables: {
      where: {
        teachingBlockVersion: {
          uuid: teachingBlockVersionUuid,
        },
      },
      options: {
        sort: [{ from: SortDirection.Asc }],
      },
    },
    context: availabilityContext,
  });

  const [, updateVersion] = useConnectEntityWithVersionMutation();

  const { subjectData } = useLoadBasicData({ pause: !teachingBlockVersionUuid });

  const store = useTeachingBlockStore();
  const versionSubjectUuids: string[] = Array.from(store.subjects.keys());

  const subjectTableData: TeachingBlockVersionTableType[] = useMemo(() => {
    return (
      subjectData?.subjects
        .filter((c) => versionSubjectUuids.includes(c.uuid))
        .map((c) => {
          const { html, label } = hexToColorOption(c.timetableConfig?.color ?? '');
          return {
            color: {
              color: html,
              label,
            },
            count: 0,
            name: c.name,
            shortName: c.shortName,
            uuid: c.uuid,
          };
        }) ?? []
    );
  }, [subjectData?.subjects, versionSubjectUuids]);

  const [selected, setSelected] = useState<TeachingBlockVersionTableType | null>(subjectTableData[0] ?? null);

  async function handleImport() {
    if (teachingBlockVersionUuid) {
      const result = await updateVersion(
        {
          organizationUuid: pimAuthClaims.getOrganization().uuid,
          versionUuid: teachingBlockVersionUuid,
          entityType: 'subject',
        },
        context,
      );
      if (!result || result.error) {
        showUserErrorToast({ text: t('common.errorToastText'), error: result.error });
      } else {
        showSuccessSaveToast();
      }
    }
  }

  const selectedExclusionUuids = exclusionsData?.teachingBlockAvailabilityExclusions
    .filter((e) => e.holder.uuid === selected?.uuid)
    .map((e) => e.uuid);

  const onExclusionEdit = (uuid: string) => {
    setSelectedExclusionUuid(uuid);
    setExclusionModalOpen(true);
  };

  const onExclusionModalClose = () => {
    setSelectedExclusionUuid(null);
    setExclusionModalOpen(false);
  };

  return (
    <Grid>
      <GridRow spacingBottom='none'>
        <SearchInput
          name='subject-search'
          placeholder={t('common.search')}
          onChange={(e) => setSearch(e.target.value)}
          value={search}
        />
        <Button
          hierarchy='tertiary'
          icon={<ImportIcon />}
          style={{ marginLeft: 'auto' }}
          onClick={handleImport}
          disabled={true}
        >
          {t('teachingBlockVersion.import', { import: t('subject.title', { count: 2 }) })}
        </Button>
      </GridRow>
      <GridRow spacingTop='m'>
        <GridColumn width={subjectTableData.length === 0 ? 12 : 6}>
          <TeachingBlockVersionSubjectsTable
            versionUuid={teachingBlockVersionUuid}
            search={search}
            versionSubjectUuids={versionSubjectUuids}
            onRowSelection={(selected) => {
              setSelected(selected);
            }}
            onShowLessonClick={(uuid) => {
              if (teachingBlockVersionUuid) {
                navigate(
                  createLessonFilterUrl({
                    context: 'epochenplan',
                    versionUuid: teachingBlockVersionUuid,
                    column: 'subject',
                    filterValue: uuid,
                  }),
                );
              }
            }}
          />
        </GridColumn>
        {subjectTableData.length !== 0 && (
          <GridColumn width={6}>
            <Suspense fallback={<LazyLoader />}>
              <Card className={styles['teaching-block-version-data']} contentPadding='none'>
                <small>{t('teachingBlockVersion.specificInfo', { for: selected?.name })}</small>
                <div className={styles['mini-tab']}>
                  <Tab
                    className={styles.tabs}
                    onValueChange={(content) => setTab(content as 'exclusion' | 'condition')}
                    tabs={[
                      {
                        title: t('exclusion.title', { count: 2 }),
                        value: 'exclusion',
                        content: <Exclusions uuids={selectedExclusionUuids ?? []} onEdit={onExclusionEdit} />,
                      },
                      {
                        title: t('conditions.title', { count: 2 }),
                        value: 'condition',
                        content: (
                          <div className='px-6 py-4'>
                            <Skeleton />
                          </div>
                        ),
                      },
                    ]}
                  />
                  <div className={styles['additional-actions']}>
                    {tab === 'exclusion' && (
                      <Tooltip content={t('exclusion.add')}>
                        <Button
                          disabled={store.readonly}
                          hierarchy='ghost'
                          icon={<AddIcon />}
                          onClick={() => setExclusionModalOpen(true)}
                        />
                      </Tooltip>
                    )}
                  </div>
                </div>
              </Card>
            </Suspense>
          </GridColumn>
        )}
      </GridRow>
      <Modal
        width='s'
        title={t('exclusion.add')}
        isOpen={exclusionModalOpen}
        onRequestClose={() => onExclusionModalClose()}
      >
        <ExclusionTimeframeForm
          type='Subject'
          holderUuid={selected?.uuid ?? ''}
          teachingBlockVersionUuid={teachingBlockVersionUuid ?? ''}
          exclusionUuid={selectedExclusionUuid}
          onClose={() => onExclusionModalClose()}
        />
      </Modal>
    </Grid>
  );
});
